import React from 'react';
import { ModifyStudyPlanFormProps, ModifyStudyPlanFormValues } from './types';
import style from './style.module.scss';
import { SecondaryButton } from '@magoosh/lib/button';
import { Formik, FormikHelpers as FormikActions } from 'formik';
import { Form } from 'react-bootstrap';
import RadioGroup from '@magoosh/lib/formik_inputs/field_radio_group';
import * as yup from 'yup';
import { useUserContext } from '@magoosh/context/user_context';
import { paths } from 'config/path_helpers';

const validationSchema = yup.object({
  mathAndVerbalMix: yup.string().required(),
  shouldIncludeWriting: yup.string().required()
});

export const ModifyStudyPlanForm: React.FC<ModifyStudyPlanFormProps> = (props) => {
  const { mathAndVerbalMix, shouldIncludeWriting, onClose, onSubmit } = props;
  const { adaptivePrepData } = useUserContext();

  const handleOnSubmit = (
    values: ModifyStudyPlanFormValues,
    { resetForm }: FormikActions<ModifyStudyPlanFormValues>
  ): void => {
    const newPlanOptions = {
      mathAndVerbalMix: values.mathAndVerbalMix,
      shouldIncludeWriting: values.shouldIncludeWriting === 'yes'
    };
    onSubmit(newPlanOptions);
    onClose();
    resetForm({ values });
  };

  const initialValues = {
    mathAndVerbalMix,
    shouldIncludeWriting: shouldIncludeWriting ? 'yes' : 'no'
  };

  return (
    <div className={style.modifyStudyPlanForm}>
      <Formik initialValues={initialValues} onSubmit={handleOnSubmit} validationSchema={validationSchema}>
        {({ isSubmitting, values, submitForm, isValid }) => (
          <Form>
            <p>
              You may change the mix of subjects that appear in your study plan. When you save a change your
              current study plan will be replaced with a new plan that matches your new mix of subjects.
            </p>
            <hr className="u-margin-A-n u-margin-B-xs" />

            <p>Math and Verbal Section Mix</p>

            <RadioGroup
              name="mathAndVerbalMix"
              className="u-margin-A-n"
              optionsArray={[
                {
                  label: 'Balanced Math and Verbal',
                  value: 'balanced',
                  labelTag: 'Recommended',
                  disabled: adaptivePrepData.isTrial
                },
                {
                  label: 'More Math, Less Verbal',
                  value: 'more_math',
                  disabled: adaptivePrepData.isTrial
                },
                {
                  label: 'More Verbal, Less Math',
                  value: 'more_verbal',
                  disabled: adaptivePrepData.isTrial
                },
                {
                  label: 'Only Math',
                  value: 'only_math',
                  disabled: adaptivePrepData.isTrial
                },
                {
                  label: 'Only Verbal',
                  value: 'only_verbal',
                  disabled: adaptivePrepData.isTrial
                }
              ]}
              value={values.mathAndVerbalMix}
            />

            <hr className="u-margin-A-n u-margin-B-xs" />

            <p>Include Writing</p>

            <RadioGroup
              name="shouldIncludeWriting"
              className="u-margin-A-n"
              optionsArray={[
                {
                  label: 'Yes',
                  value: 'yes',
                  labelTag: 'Recommended',
                  disabled: adaptivePrepData.isTrial
                },
                {
                  label: 'No',
                  value: 'no',
                  disabled: adaptivePrepData.isTrial
                }
              ]}
              value={values.shouldIncludeWriting}
            />

            <hr className="u-margin-A-n u-margin-B-xs" />

            {adaptivePrepData.isTrial ? (
              <SecondaryButton href={paths.plans()} target="_blank">
                Upgrade
              </SecondaryButton>
            ) : (
              <SecondaryButton disabled={isSubmitting || !isValid} onClick={submitForm}>
                Save and Modify
              </SecondaryButton>
            )}
            <a className={style.cancelButton} onClick={onClose}>
              Cancel
            </a>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ModifyStudyPlanForm;
